import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import NavigationBar from '../NavigationBar';
import axios from '../axios';
import { Container, Row, Col, Table, Button, ButtonGroup, Form, Badge, Tabs, Tab, Alert, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { formatNumberWithCommas } from '../utilities';
import { notify, errorMessage, confirmAction } from '../notify';
import { sheetRows, printSheet, downloadSheet, measurementText } from './board_plan_sheet';
import { LangContext, useT, loadLang, saveLang } from './board_plan_lang';

// Board Making Plan: the factory manager's own daily plan and evening day close.
//   Plan tab:  search job numbers, tick lines into the day, set roll size, setting, qty and the paper on each
//              machine station. Grouped by roll size; prints / downloads in his Excel sheet format.
//   Day close: made qty per job, the roll notebook (roll no -> used up ✗ or balance kg -> station), check, close.
// Separate from Production Planning / Board Making: nothing here changes PP, BM or challans.
// English / Bangla toggle (board_plan_lang.js); paper names, job numbers and station codes stay as they are.

const BANGLA_DIGITS = '০১২৩৪৫৬৭৮৯';
// roll search: a typed weight may differ from the stock weight by this much (scale difference)
const ROLL_WEIGHT_TOLERANCE_KG = 20;
const toLatin = (s) => String(s ?? '').replace(/[০-৯]/g, (d) => String(BANGLA_DIGITS.indexOf(d)));
const numOrNull = (s) => {
    const t = toLatin(s).trim();
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
};
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const kg = (n) => (n == null ? '-' : `${formatNumberWithCommas(Math.round(Number(n) || 0))}`);
const bdToday = () => new Date(Date.now() + 6 * 3600 * 1000).toISOString().slice(0, 10);
const addDays = (d, n) => {
    const t = new Date(`${d}T00:00:00Z`);
    t.setUTCDate(t.getUTCDate() + n);
    return t.toISOString().slice(0, 10);
};
// Roll size for a board: carton (width + height) or sheet width, x setting, in inches,
// then the next even size with at least 1" spare (same as the server)
const CM_PER = { Inch: 2.54, MM: 0.1, Yard: 91.44 };
const toCm = (v, unit) => (Number(v) || 0) * (CM_PER[unit] || 1);
const rollSizeFor = (job, setting) => {
    const w = toCm(job.Width, job.Unit);
    const h = toCm(job.Height, job.Unit);
    if (!(w > 0)) return null;
    return Math.ceil((((h > 0 ? w + h : w) * (Number(setting) || 1)) / 2.54 + 1) / 2) * 2;
};
// A 7-ply job is two lines (Part 3 and Part 4) made the same day: qty, setting, size and hold are the same on both
const PART_SHARED = ['RollSize', 'Setting', 'RollShare', 'PlannedQty', 'MadeQty', 'IsHold', 'NotMadeReason'];
const applyJobPatch = (jobs, job, patch) => {
    const shared = Object.fromEntries(Object.entries(patch).filter(([k]) => PART_SHARED.includes(k)));
    return jobs.map((j) => {
        if (j.JobLineID === job.JobLineID) return { ...j, ...patch, papers: patch.papers ? { ...j.papers, ...patch.papers } : j.papers };
        if (job.Part && j.Part && j.OrderGsmId === job.OrderGsmId && Object.keys(shared).length) return { ...j, ...shared };
        return j;
    });
};
function PartBadge({ job }) {
    const t = useT();
    if (!job.Part) return null;
    return <Badge bg="warning" text="dark" className="ms-1" title={t('partTitle')}>{t('part', { n: job.Part })}</Badge>;
}
const VERDICT_BG ={ ok: '#d1e7dd', warn: '#fff3cd', bad: '#f8d7da', none: 'transparent' };
const cell = { padding: '3px 5px', verticalAlign: 'middle' };
const head = { ...cell, backgroundColor: '#d1e7dd', whiteSpace: 'nowrap' };

function BoardPlan() {
    const [lang, setLang] = useState(loadLang);
    const changeLang = (l) => { setLang(l); saveLang(l); };
    return (
        <LangContext.Provider value={lang}>
            <BoardPlanPage lang={lang} changeLang={changeLang} />
        </LangContext.Provider>
    );
}

function BoardPlanPage({ lang, changeLang }) {
    const t = useT();
    const navigate = useNavigate();
    const [options, setOptions] = useState({ positions: [], papers: [], useKinds: ['Board', 'EFlute', 'Cutting', 'Other'], isAdmin: false });
    const [date, setDate] = useState(bdToday());
    const [data, setData] = useState({ plan: null, jobs: [], rows: [], runNotes: {} });
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState('plan');
    const [rolls, setRolls] = useState([]);
    const [days, setDays] = useState([]);

    useEffect(() => {
        axios.get(`${window.backendUrl}/day_plan/options`)
            .then((res) => setOptions(res.data))
            .catch((err) => notify.error(errorMessage(err, t('loadFail'))));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadRolls = useCallback(() => {
        axios.get(`${window.backendUrl}/day_plan/rolls`)
            .then((res) => setRolls(res.data.rolls || []))
            .catch(() => {});
    }, []);
    useEffect(() => { loadRolls(); }, [loadRolls]);

    const loadDays = useCallback(() => {
        axios.get(`${window.backendUrl}/day_plan/list`).then((res) => setDays(res.data.plans || [])).catch(() => {});
    }, []);
    useEffect(() => { loadDays(); }, [loadDays]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${window.backendUrl}/day_plan`, { params: { date } });
            setData({ plan: res.data.plan, jobs: res.data.jobs || [], rows: res.data.rows || [], runNotes: res.data.runNotes || {} });
        } catch (err) {
            notify.error(errorMessage(err, t('loadFail')));
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);
    useEffect(() => { load(); }, [load]);

    const papersById = useMemo(() => Object.fromEntries(options.papers.map((p) => [p.GSMID, p])), [options.papers]);
    const paperName = useCallback((id) => {
        const p = papersById[id];
        return p ? `${p.Name} ${p.GSMValue}`.trim() : '';
    }, [papersById]);

    const closed = data.plan?.Status === 'Closed';
    const reloadAll = () => { load(); loadDays(); loadRolls(); };

    return (
        <>
            <NavigationBar />
            <Container fluid>
                <Row className="justify-content-center mt-3">
                    <Col md={12} lg={11}>
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                            <h4 className="mb-0 me-3">{t('title')}</h4>
                            <Button size="sm" variant="outline-secondary" onClick={() => setDate(addDays(date, -1))}>‹</Button>
                            <Form.Control type="date" size="sm" style={{ width: 160 }} value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
                            <Button size="sm" variant="outline-secondary" onClick={() => setDate(addDays(date, 1))}>›</Button>
                            <Button size="sm" variant={date === bdToday() ? 'secondary' : 'outline-secondary'} onClick={() => setDate(bdToday())}>{t('today')}</Button>
                            <Button size="sm" variant={date === addDays(bdToday(), 1) ? 'secondary' : 'outline-secondary'} onClick={() => setDate(addDays(bdToday(), 1))}>{t('tomorrow')}</Button>
                            {data.plan
                                ? <Badge bg={closed ? 'success' : 'warning'} text={closed ? undefined : 'dark'}>{closed ? t('closed') : t('open')}</Badge>
                                : <Badge bg="light" text="dark">{t('noPlanYet')}</Badge>}
                            <Form.Select size="sm" style={{ width: 240 }} value="" onChange={(e) => e.target.value && setDate(e.target.value)}>
                                <option value="">{t('recentDays')}</option>
                                {days.map((d) => (
                                    <option key={d.PlanID} value={d.PlanDate}>
                                        {t('dayItem', { date: d.PlanDate, status: d.Status === 'Closed' ? t('closed') : t('open'), jobs: d.jobs, rolls: d.rolls })}
                                    </option>
                                ))}
                            </Form.Select>
                            {loading && <span className="text-muted small">{t('loading')}</span>}
                            <Button size="sm" variant="outline-primary" className="ms-auto" onClick={() => navigate('/paper-consumption')}>{t('linkConsumption')}</Button>
                            <Button size="sm" variant="outline-primary" onClick={() => navigate('/paper-balance')}>{t('linkBalance')}</Button>
                            <ButtonGroup size="sm">
                                <Button variant={lang === 'en' ? 'dark' : 'outline-dark'} onClick={() => changeLang('en')}>English</Button>
                                <Button variant={lang === 'bn' ? 'dark' : 'outline-dark'} onClick={() => changeLang('bn')}>বাংলা</Button>
                            </ButtonGroup>
                        </div>
                        <Tabs activeKey={tab} onSelect={(k) => setTab(k)} className="mb-3">
                            <Tab eventKey="plan" title={t('tabPlan')}>
                                <PlanTab date={date} data={data} setData={setData} closed={closed} options={options} paperName={paperName} rolls={rolls} reload={reloadAll} />
                            </Tab>
                            <Tab eventKey="close" title={t('tabClose')}>
                                {tab === 'close' && (
                                    <CloseTab date={date} data={data} setData={setData} closed={closed} options={options} paperName={paperName} rolls={rolls} reload={reloadAll} />
                                )}
                            </Tab>
                        </Tabs>
                    </Col>
                </Row>
            </Container>
        </>
    );
}

// ====================================================================== Plan tab
function PlanTab({ date, data, setData, closed, options, paperName, rolls, reload }) {
    const t = useT();
    const [q, setQ] = useState('');
    const [found, setFound] = useState(null);
    const [isPending, setIsPending] = useState(true);
    const [ticked, setTicked] = useState({});
    const [searching, setSearching] = useState(false);
    const [adding, setAdding] = useState(false);
    const [showList, setShowList] = useState(true);
    const positions = options.positions;

    // No text: all jobs (every company) with a balance left, newest first. Text: job numbers or party / buyer / item.
    const [filters, setFilters] = useState({ party: '', ply: '', size: '', from: '', to: '' });
    const filtersRef = useRef(filters);
    filtersRef.current = filters;
    const search = useCallback(async (text) => {
        setSearching(true);
        try {
            const f = filtersRef.current;
            const res = await axios.get(`${window.backendUrl}/day_plan/job_lookup`, {
                params: { q: text || '', party: f.party.trim(), ply: f.ply, size: toLatin(f.size).trim(), from: f.from, to: f.to },
            });
            const lines = res.data.lines || [];
            setFound(lines);
            setIsPending(!!res.data.pending);
            setShowList(true);
            // a typed search ticks the lines that still have a balance; the full pending list starts unticked
            setTicked(res.data.pending ? {} : Object.fromEntries(lines.filter((l) => Number(l.balance) > 0).map((l) => [l.OrderGsmId, true])));
        } catch (err) {
            notify.error(errorMessage(err, t('searchFail')));
        }
        setSearching(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => { if (!closed) search(''); }, [closed, search]);

    // The manager marks the plan final; it stays editable and later lines are marked
    const confirmPlan = async () => {
        try {
            await axios.post(`${window.backendUrl}/day_plan/${data.plan.PlanID}/confirm`, {});
            reload();
        } catch (err) { notify.error(errorMessage(err, t('notSaved'))); }
    };

    const addTicked = async () => {
        const ids = Object.keys(ticked).filter((k) => ticked[k]).map(Number);
        if (!ids.length) return notify.error(t('tickOne'));
        setAdding(true);
        try {
            const res = await axios.post(`${window.backendUrl}/day_plan/jobs`, { date, orderGsmIds: ids });
            notify(t('addedLines', { added: res.data.added }) + (res.data.skipped ? t('alreadyIn', { n: res.data.skipped }) : ''));
            setTicked({});
            reload();
        } catch (err) {
            notify.error(errorMessage(err, t('addFail')));
        }
        setAdding(false);
    };

    // Save one field of a job line; the screen updates at once
    const saveJob = async (job, patch) => {
        setData((d) => ({ ...d, jobs: applyJobPatch(d.jobs, job, patch) }));
        try {
            await axios.post(`${window.backendUrl}/day_plan/jobs/${job.JobLineID}/update`, patch);
        } catch (err) {
            notify.error(errorMessage(err, t('notSaved')));
            reload();
        }
    };

    const removeJob = async (job) => {
        if (!(await confirmAction({ title: t('removeTitle'), message: `${job.JobNumber} — ${job.ItemName || ''} ${measurementText(job)}`, confirmLabel: t('remove') }))) return;
        try {
            await axios.post(`${window.backendUrl}/day_plan/jobs/${job.JobLineID}/remove`, {});
            reload();
        } catch (err) { notify.error(errorMessage(err, t('removeFail'))); }
    };

    // a part of a 7-ply job has papers only on its own positions
    const usesPos = (j, code) => !j.Part || (options.partPositions?.[j.Part] || []).includes(code);
    const copyPapersToGroup = (from, group) => {
        group.filter((j) => j.JobLineID !== from.JobLineID).forEach((j) => {
            const papers = {};
            positions.filter((p) => usesPos(j, p.Code)).forEach((p) => { papers[p.Code] = from.papers?.[p.Code] || null; });
            saveJob(j, { papers });
        });
    };

    const groups = useMemo(() => {
        const by = {};
        data.jobs.forEach((j) => {
            const k = j.RollSize == null || j.RollSize === '' ? 'none' : String(Number(j.RollSize));
            (by[k] = by[k] || []).push(j);
        });
        return Object.keys(by).sort((a, b) => (a === 'none' ? 1 : b === 'none' ? -1 : Number(a) - Number(b))).map((k) => ({ size: k, jobs: by[k] }));
    }, [data.jobs]);

    // Stock check: kg needed per paper vs kg in stock on rolls 0-3" wider than the size
    const stockFor = useCallback((gsmId, size) => rolls
        .filter((r) => Number(r.GSMID) === Number(gsmId) && Number(r.RollSize) >= size && Number(r.RollSize) - size <= 3)
        .reduce((s, r) => s + (Number(r.CurrentWeight) || 0), 0), [rolls]);
    const takeUp = useMemo(() => Object.fromEntries(positions.map((p) => [p.Code, Number(p.TakeUpFactor) || 1])), [positions]);
    const needFor = (group) => {
        const need = {};
        group.jobs.filter((j) => !Number(j.IsHold)).forEach((j) => {
            positions.forEach((p) => {
                const g = j.papers?.[p.Code];
                if (!g) return;
                const gsm = Number(String(paperName(g)).split(' ').pop()) || 0;
                need[g] = (need[g] || 0) + (Number(j.PlannedQty) || 0) * (Number(j.sqmPerPiece) || 0) * takeUp[p.Code] * gsm / 1000;
            });
        });
        return need;
    };

    const rowsForSheet = () => sheetRows(groups.flatMap((g) => g.jobs), paperName, positions);
    const inPlan = new Set(data.jobs.map((j) => j.OrderGsmId));

    return (
        <>
            {!closed && (
                <div className="mb-3 p-2" style={{ border: '1px solid #ccc', borderRadius: 6 }}>
                    <InputGroup size="sm" style={{ maxWidth: 700 }}>
                        <Form.Control
                            placeholder={t('searchPlaceholder')}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') search(q); }}
                        />
                        <Button onClick={() => search(q)} disabled={searching}>{searching ? t('searching') : t('search')}</Button>
                        {!isPending && <Button variant="outline-secondary" onClick={() => { setQ(''); search(''); }}>{t('showPending')}</Button>}
                    </InputGroup>
                    <div className="d-flex flex-wrap align-items-center gap-2 mt-2 small">
                        <span className="text-muted">{t('filters')}:</span>
                        <Form.Control size="sm" style={{ width: 170 }} placeholder={t('fParty')} value={filters.party}
                            onChange={(e) => setFilters((x) => ({ ...x, party: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') search(q); }} />
                        <Form.Select size="sm" style={{ width: 110 }} value={filters.ply} onChange={(e) => setFilters((x) => ({ ...x, ply: e.target.value }))}>
                            <option value="">{t('fPly')}</option>
                            {['Ply 1', 'Ply 2', 'Ply 3', 'Ply 5', 'Ply 7', 'PP'].map((p) => <option key={p} value={p}>{p}</option>)}
                        </Form.Select>
                        <Form.Control size="sm" style={{ width: 100 }} placeholder={t('fSize')} value={filters.size}
                            onChange={(e) => setFilters((x) => ({ ...x, size: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') search(q); }} />
                        <span className="text-muted">{t('fFrom')}</span>
                        <Form.Control type="date" size="sm" style={{ width: 150 }} value={filters.from} onChange={(e) => setFilters((x) => ({ ...x, from: e.target.value }))} />
                        <span className="text-muted">{t('fTo')}</span>
                        <Form.Control type="date" size="sm" style={{ width: 150 }} value={filters.to} onChange={(e) => setFilters((x) => ({ ...x, to: e.target.value }))} />
                        <Button size="sm" variant="outline-primary" onClick={() => search(q)}>{t('fApply')}</Button>
                        <Button size="sm" variant="link" className="p-0" onClick={() => { filtersRef.current = { party: '', ply: '', size: '', from: '', to: '' }; setFilters(filtersRef.current); search(q); }}>{t('fClear')}</Button>
                    </div>
                    {found && (
                        <div className="mt-2">
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <strong className="small">{isPending ? t('pendingTitle', { n: found.length }) : t('searchTitle', { n: found.length })}</strong>
                                <Button size="sm" variant="link" className="p-0" onClick={() => setShowList((v) => !v)}>{showList ? t('hideList') : t('showList')}</Button>
                            </div>
                            {showList && (found.length === 0 ? <div className="text-muted small">{t('noJobFound')}</div> : (
                                <>
                                    <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #dee2e6' }}>
                                        <Table bordered size="sm" className="mb-0" style={{ fontSize: 13 }}>
                                            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}><tr>
                                                <th style={head}></th><th style={head}>{t('colJob')}</th><th style={head}>{t('colCompany')}</th><th style={head}>{t('colClientBuyer')}</th>
                                                <th style={head}>{t('colItem')}</th><th style={head}>{t('colSpec')}</th><th style={head}>{t('colMeasurement')}</th>
                                                <th style={head}>{t('colRoll')}</th><th style={head}>{t('colOrderQty')}</th><th style={head}>{t('colAlreadyMade')}</th><th style={head}>{t('colLeft')}</th>
                                            </tr></thead>
                                            <tbody>
                                                {found.map((l) => {
                                                    const already = inPlan.has(l.OrderGsmId);
                                                    const done = Number(l.balance) <= 0;
                                                    return (
                                                        <tr key={l.OrderGsmId} style={{ opacity: already || done ? 0.5 : 1 }}>
                                                            <td style={cell}>
                                                                {already ? <Badge bg="secondary">{t('inPlan')}</Badge> : (
                                                                    <Form.Check checked={!!ticked[l.OrderGsmId]} onChange={(e) => setTicked((x) => ({ ...x, [l.OrderGsmId]: e.target.checked }))} />
                                                                )}
                                                            </td>
                                                            <td style={cell}>{l.JobNumber}</td>
                                                            <td style={cell}>{l.OrderTeam || '-'}</td>
                                                            <td style={cell}>{l.PartyName}{l.BuyerName ? ` / ${l.BuyerName}` : ''}</td>
                                                            <td style={cell}>{l.ItemName}</td>
                                                            <td style={cell}>{l.Specification}</td>
                                                            <td style={cell}>{measurementText(l)}</td>
                                                            <td style={cell}>{l.defaultRollSize || '-'}</td>
                                                            <td style={cell}>{formatNumberWithCommas(l.OrderQuantity)}</td>
                                                            <td style={cell}>{formatNumberWithCommas(l.alreadyMade)}</td>
                                                            <td style={cell}><strong>{done ? t('doneLabel') : formatNumberWithCommas(l.balance)}</strong></td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                    <Button size="sm" variant="success" className="mt-2" onClick={addTicked} disabled={adding}>{adding ? t('adding') : t('addTicked', { date })}</Button>
                                </>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="d-flex gap-2 mb-2 align-items-center">
                {data.plan?.ConfirmedAt
                    ? <Badge bg="success">{t('confirmedBy', { name: data.plan.ConfirmedByName || '?', time: new Date(data.plan.ConfirmedAt * 1000).toLocaleString() })}</Badge>
                    : (!closed && data.jobs.length > 0 && <Button size="sm" variant="success" onClick={confirmPlan}>{t('confirmPlan')}</Button>)}
                <Button size="sm" variant="outline-dark" disabled={!data.jobs.length} onClick={() => { if (!printSheet(date, rowsForSheet())) notify.error(t('allowPopups')); }}>{t('printSheet')}</Button>
                <Button size="sm" variant="outline-dark" disabled={!data.jobs.length} onClick={() => downloadSheet(date, rowsForSheet()).catch(() => notify.error(t('excelFail')))}>{t('downloadExcel')}</Button>
                <span className="text-muted small align-self-center">
                    {t('linesPlanned', { lines: data.jobs.length, pcs: formatNumberWithCommas(data.jobs.filter((j) => !Number(j.IsHold) && Number(j.Part) !== 3).reduce((s, j) => s + (Number(j.PlannedQty) || 0), 0)) })}
                </span>
            </div>

            {!data.jobs.length && <Alert variant="light">{t('noJobs')}</Alert>}

            {groups.map((g) => {
                const need = needFor(g);
                const size = Number(g.size);
                return (
                    <div key={g.size} className="mb-3">
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                            <strong style={{ fontSize: 16 }}>{g.size === 'none' ? t('noRollSize') : t('rollGroup', { size: g.size })}</strong>
                            <span className="text-muted small">{t('jobsCount', { n: g.jobs.length })}</span>
                            {g.size !== 'none' && Object.keys(need).map((gid) => {
                                const have = stockFor(gid, size);
                                const short = have < need[gid];
                                return (
                                    <Badge key={gid} bg={short ? 'danger' : 'light'} text={short ? undefined : 'dark'} style={{ fontWeight: 'normal' }}>
                                        {t('needStock', { paper: paperName(gid), need: kg(need[gid]), have: kg(have) })}
                                    </Badge>
                                );
                            })}
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <Table bordered size="sm" style={{ fontSize: 13, minWidth: 1200 }}>
                                <thead><tr>
                                    <th style={head}>{t('colJob')}</th><th style={head}>{t('colClientBuyer')}</th><th style={head}>{t('colItemSpec')}</th><th style={head}>{t('colMeasurement')}</th>
                                    <th style={head}>{t('colOrderMade')}</th><th style={head}>{t('colSetting')}</th><th style={head}>{t('colRoll')}</th>
                                    <th style={head} title={t('sameRollTitle')}>{t('colSameRoll')}</th><th style={head}>{t('colPlanQty')}</th>
                                    {positions.map((p) => <th key={p.Code} style={head} title={p.Label}>{p.Code}</th>)}
                                    <th style={head}>{t('colHold')}</th><th style={head}></th>
                                </tr></thead>
                                <tbody>
                                    {g.jobs.map((j) => (
                                        <tr key={j.JobLineID} style={{ backgroundColor: Number(j.IsHold) ? '#eee' : undefined }}>
                                            <td style={cell}>{j.JobNumber}<PartBadge job={j} />{Number(j.AddedMidDay) ? <Badge bg="info" className="ms-1">{t('midDay')}</Badge> : null}</td>
                                            <td style={cell}>{j.PartyName}{j.BuyerName ? ` / ${j.BuyerName}` : ''}</td>
                                            <td style={cell}>{j.ItemName} · {j.Specification}</td>
                                            <td style={cell}>{measurementText(j)}</td>
                                            <td style={cell}>{formatNumberWithCommas(j.OrderQuantity)} / {formatNumberWithCommas(j.factoryMadeBefore)}</td>
                                            <td style={cell}><NumInput disabled={closed} value={j.Setting} width={55} onSave={(v) => { const size = rollSizeFor(j, v); saveJob(j, size ? { Setting: v, RollSize: size } : { Setting: v }); }} /></td>
                                            <td style={cell}><NumInput disabled={closed} value={j.RollSize} width={60} onSave={(v) => saveJob(j, { RollSize: v })} /></td>
                                            <td style={cell} title={t('sameRollTitle')}><NumInput disabled={closed} value={j.RollShare} width={45} onSave={(v) => saveJob(j, { RollShare: v > 0 ? Math.round(v) : null })} /></td>
                                            <td style={cell}><NumInput disabled={closed} value={j.PlannedQty} width={75} onSave={(v) => saveJob(j, { PlannedQty: v ?? 0 })} /></td>
                                            {positions.map((p) => (
                                                <td key={p.Code} style={cell}>
                                                    {usesPos(j, p.Code)
                                                        ? <PaperSelect disabled={closed} papers={options.papers} value={j.papers?.[p.Code] || ''} onChange={(v) => saveJob(j, { papers: { [p.Code]: v || null } })} />
                                                        : <span className="text-muted">—</span>}
                                                </td>
                                            ))}
                                            <td style={cell}><Form.Check disabled={closed} checked={!!Number(j.IsHold)} onChange={(e) => saveJob(j, { IsHold: e.target.checked ? 1 : 0 })} /></td>
                                            <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                                                {!closed && g.jobs.length > 1 && <Button size="sm" variant="link" className="p-0 me-2" title={t('copyDownTitle')} onClick={() => copyPapersToGroup(j, g.jobs)}>{t('copyDown')}</Button>}
                                                {!closed && <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => removeJob(j)}>✕</Button>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                );
            })}
        </>
    );
}

// Number box that saves when you leave it (or press Enter)
function NumInput({ value, onSave, width = 70, disabled, placeholder }) {
    const [v, setV] = useState(value ?? '');
    useEffect(() => { setV(value ?? ''); }, [value]);
    const commit = () => {
        const n = numOrNull(v);
        const old = value === null || value === undefined || value === '' ? null : Number(value);
        if (n === old) return;
        if (String(v).trim() !== '' && n === null) { setV(value ?? ''); return; }
        onSave(n);
    };
    return (
        <Form.Control size="sm" style={{ width, padding: '1px 4px' }} value={v} disabled={disabled} placeholder={placeholder}
            onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />
    );
}

function PaperSelect({ papers, value, onChange, disabled }) {
    return (
        <Form.Select size="sm" style={{ minWidth: 115, padding: '1px 22px 1px 4px' }} value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
            <option value="">—</option>
            {papers.map((p) => <option key={p.GSMID} value={p.GSMID}>{p.Name} {p.GSMValue}</option>)}
        </Form.Select>
    );
}

// ====================================================================== Day close tab
const blankRow = () => ({ key: Math.random().toString(36).slice(2), RollID: null, UseKind: 'Board', Position: '', RunSize: '', ShareRuns: '', IsFinished: false, EndKg: '', CoreKg: '', ApprovedBy: '', RefJob: '', Note: '' });

function CloseTab({ date, data, setData, closed, options, paperName, rolls, reload }) {
    const t = useT();
    const positions = options.positions;
    const [rows, setRows] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [check, setCheck] = useState(null);
    const [reasons, setReasons] = useState({});
    const [closing, setClosing] = useState(false);
    const [entry, setEntry] = useState('');
    const entryRef = useRef(null);

    // roll catalogue: active rolls + the rolls already in this notebook
    const rollMap = useMemo(() => {
        const m = {};
        rolls.forEach((r) => { m[r.RollID] = r; });
        data.rows.forEach((r) => { if (!m[r.RollID]) m[r.RollID] = { RollID: r.RollID, RollNo: r.RollNo, RollCode: r.RollCode, GSMID: r.GSMID, RollSize: r.RollSize, CurrentWeight: r.CurrentWeight, Status: r.RollStatus }; });
        return m;
    }, [rolls, data.rows]);

    useEffect(() => {
        setRows(data.rows.map((r) => ({
            key: `u${r.UseID}`, RollID: r.RollID, UseKind: r.UseKind || 'Board', Position: r.Position || '', RunSize: r.RunSize ?? '', ShareRuns: r.ShareRuns || '', IsFinished: !!Number(r.IsFinished),
            EndKg: r.EndKg ?? '', CoreKg: Number(r.CoreKg) ? r.CoreKg : '', ApprovedBy: r.ApprovedBy || '', RefJob: r.RefJob || '', Note: r.Note || '',
            StartKg: r.StartKg, UsedKg: r.UsedKg,
        })));
        setReasons(data.runNotes || {});
        setDirty(false);
        setCheck(null);
    }, [data.rows, data.runNotes, data.plan?.PlanID]);

    useEffect(() => {
        if (!data.plan?.PlanID) return;
        if (closed) {
            axios.get(`${window.backendUrl}/day_plan/${data.plan.PlanID}/preview`).then((res) => setCheck(res.data)).catch(() => {});
        }
    }, [closed, data.plan?.PlanID]);

    const rollText = (r) => (r ? `${paperName(r.GSMID)} · ${Number(r.RollSize)}"` : '');

    // Search the way the notebook is written: "66 946" (size + weight), "m-64 120 57", or a roll no.
    // Each number must be the roll size, the GSM, or close to the weight; words must be in the paper name.
    // Closest weight comes first, so his own short codes don't matter.
    const usedToday = useMemo(() => new Set(rows.map((r) => r.RollID)), [rows]);
    const matches = useMemo(() => {
        // "m-64", "m·63", "Cn.50" -> words and numbers; a dot between digits stays a decimal (33.5)
        const s = toLatin(entry).trim().toLowerCase()
            .replace(/(\d)\.(\d)/g, '$1§$2').replace(/[-·.,/\\_:]+/g, ' ').replace(/§/g, '.');
        if (!s) return [];
        const tokens = s.split(/\s+/).filter(Boolean);
        const nums = tokens.filter((w) => /^\d+(\.\d+)?$/.test(w)).map(Number);
        const words = tokens.filter((w) => !/^\d+(\.\d+)?$/.test(w));
        const single = tokens.length === 1 && nums.length === 1 ? nums[0] : null;
        const out = [];
        Object.values(rollMap).forEach((r) => {
            if (r.Status && r.Status !== 'Active' && !usedToday.has(r.RollID)) return;
            const name = paperName(r.GSMID).toLowerCase();
            // with numbers typed, his own codes (TB, Cn, BL...) only help the order; without numbers it is a name search
            const wordHits = words.filter((w) => name.includes(w)).length;
            if (!nums.length && wordHits < words.length) return;
            const size = Number(r.RollSize);
            const gsm = Number(name.split(' ').pop()) || 0;
            const w = Number(r.CurrentWeight) || 0;
            const isNo = single != null && String(r.RollNo) === String(single);
            let dist = 0;
            let sizeHit = 0;
            let weightHit = 0;
            if (!isNo) {
                for (const n of nums) {
                    if (n === size) { sizeHit += 1; continue; }
                    if (n === gsm) continue;
                    const d = Math.abs(w - n);
                    if (d > ROLL_WEIGHT_TOLERANCE_KG) return;
                    dist += d;
                    weightHit += 1;
                }
            }
            out.push({ roll: r, isNo, dist, sizeHit, weightHit, wordHits, w });
        });
        out.sort((a, b) => (b.isNo - a.isNo) || (a.dist - b.dist) || (b.sizeHit - a.sizeHit) || (b.wordHits - a.wordHits) || (a.w - b.w));
        return out.slice(0, 40);
    }, [entry, rollMap, paperName, usedToday]);
    const [pick, setPick] = useState(-1);
    useEffect(() => { setPick(-1); }, [entry]);
    // Enter adds straight away only when there is no doubt: the roll no, one match, or one exact weight match
    const sureMatch = () => {
        if (pick >= 0 && matches[pick]) return matches[pick].roll;
        if (matches[0]?.isNo || matches.length === 1) return matches[0]?.roll || null;
        const exact = matches.filter((m) => m.weightHit && m.dist === 0);
        return exact.length === 1 ? exact[0].roll : null;
    };

    const lastPosition = () => [...rows].reverse().find((r) => r.Position)?.Position || '';
    const addRoll = (roll) => {
        if (!roll) return notify.error(t('rollNotFound'));
        setRows((rs) => [...rs, { ...blankRow(), RollID: roll.RollID, Position: lastPosition() }]);
        setDirty(true);
        setEntry('');
        setTimeout(() => entryRef.current?.focus(), 0);
    };
    const setRow = (key, patch) => { setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r))); setDirty(true); };
    const delRow = (key) => { setRows((rs) => rs.filter((r) => r.key !== key)); setDirty(true); };
    const moveRow = (idx, dir) => {
        setRows((rs) => {
            const n = [...rs];
            const j = idx + dir;
            if (j < 0 || j >= n.length) return rs;
            [n[idx], n[j]] = [n[j], n[idx]];
            return n;
        });
        setDirty(true);
    };

    const saveJob = async (job, patch) => {
        setData((d) => ({ ...d, jobs: applyJobPatch(d.jobs, job, patch) }));
        setCheck(null);
        try { await axios.post(`${window.backendUrl}/day_plan/jobs/${job.JobLineID}/update`, patch); } catch (err) { notify.error(errorMessage(err, t('notSaved'))); reload(); }
    };

    const saveNotebook = async () => {
        if (!data.plan) return false;
        setSaving(true);
        try {
            const saved = await axios.post(`${window.backendUrl}/day_plan/${data.plan.PlanID}/rolls`, {
                version: data.plan.Version ?? 0,
                rows: rows.map((r) => ({
                    RollID: r.RollID, UseKind: r.UseKind, Position: r.UseKind === 'Board' ? r.Position || null : null,
                    RunSize: r.UseKind === 'Board' && !r.ShareRuns ? numOrNull(r.RunSize) : null,
                    ShareRuns: r.UseKind === 'Board' ? r.ShareRuns || null : null,
                    IsFinished: r.IsFinished ? 1 : 0, EndKg: r.IsFinished ? null : numOrNull(r.EndKg), CoreKg: r.IsFinished ? numOrNull(r.CoreKg) || 0 : 0,
                    ApprovedBy: r.ApprovedBy, RefJob: r.RefJob, Note: r.Note,
                })),
            });
            // keep the version the server now has, so the next save from this page is accepted
            setData((d) => ({ ...d, plan: { ...d.plan, Version: saved.data?.version ?? d.plan.Version } }));
            await axios.post(`${window.backendUrl}/day_plan/${data.plan.PlanID}/run_notes`, {
                notes: Object.entries(reasons).map(([rollSize, reason]) => ({ rollSize, reason })),
            });
            setDirty(false);
            setSaving(false);
            return true;
        } catch (err) {
            setSaving(false);
            // the notebook was changed by Claude or another screen after this page loaded it
            if (err?.response?.data?.code === 'changed_elsewhere') {
                if (await confirmAction({ title: t('changedTitle'), message: t('changedMsg'), confirmLabel: t('reloadBtn') })) reload();
                return false;
            }
            notify.error(errorMessage(err, t('saveFail')));
            return false;
        }
    };

    const runCheck = async () => {
        if (dirty && !(await saveNotebook())) return;
        try {
            const res = await axios.get(`${window.backendUrl}/day_plan/${data.plan.PlanID}/preview`);
            setCheck(res.data);
            const bySeq = Object.fromEntries((res.data.rows || []).map((r) => [r.Seq, r]));
            setRows((rs) => rs.map((r, i) => {
                const x = bySeq[i + 1] || {};
                return { ...r, StartKg: x.StartKg, UsedKg: x.UsedKg, runSize: x.runSize, error: x.error, warning: x.warning, errorMsg: x.errorMsg, warningMsg: x.warningMsg };
            }));
        } catch (err) { notify.error(errorMessage(err, t('checkFail'))); }
    };

    const closeDay = async () => {
        if (!(await confirmAction({ title: t('closeTitle', { date }), message: t('closeMsg'), confirmLabel: t('closeDay'), variant: 'success' }))) return;
        if (dirty && !(await saveNotebook())) return;
        setClosing(true);
        try {
            await axios.post(`${window.backendUrl}/day_plan/${data.plan.PlanID}/close`, {});
            notify(t('closeOk'));
            reload();
        } catch (err) {
            const errs = err?.response?.data?.errors;
            notify.error(errs && errs.length ? t.msg(errs[0]) : errorMessage(err, t('closeFail')));
            if (errs) setCheck((c) => ({ ...(c || {}), errors: errs }));
        }
        setClosing(false);
    };

    const reopen = async () => {
        if (!(await confirmAction({ title: t('reopenTitle', { date }), message: t('reopenMsg'), confirmLabel: t('reopenBtn') }))) return;
        try {
            await axios.post(`${window.backendUrl}/day_plan/${data.plan.PlanID}/reopen`, {});
            notify(t('reopenOk'));
            reload();
        } catch (err) {
            const errs = err?.response?.data?.errors;
            notify.error(errs && errs.length ? t.msg(errs[0]) : errorMessage(err, t('reopenFail')));
        }
    };

    if (!data.plan) return <Alert variant="light">{t('noPlanFor', { date })}</Alert>;

    const sortedJobs = [...data.jobs].sort((a, b) => (Number(a.RollSize) || 999) - (Number(b.RollSize) || 999));
    // run sizes an oversize roll can be booked to: the day's job sizes not wider than the roll
    const planSizes = [...new Set(data.jobs.filter((j) => !Number(j.IsHold) && Number(j.RollSize) > 0).map((j) => r2(j.RollSize)))].sort((a, b) => b - a);
    const canClose = check && !check.closed && !(check.errors || []).length && !dirty;
    const pct = (p) => (p == null ? '-' : `${p}%`);
    let prevSize = null;

    return (
        <>
            <h6>{t('secMade')}</h6>
            <div style={{ overflowX: 'auto' }}>
                <Table bordered size="sm" style={{ fontSize: 13, minWidth: 900 }}>
                    <thead><tr>
                        <th style={head}>{t('colRoll')}</th><th style={head}>{t('colJob')}</th><th style={head}>{t('colClient')}</th><th style={head}>{t('colItemMeasurement')}</th>
                        <th style={head}>{t('colPlanned')}</th><th style={head}>{t('colMade')}</th><th style={head}>{t('colHold')}</th><th style={head}>{t('colReason')}</th>
                    </tr></thead>
                    <tbody>
                        {sortedJobs.map((j) => {
                            const made = j.MadeQty;
                            const short = made !== null && made !== undefined && Number(made) < Number(j.PlannedQty);
                            return (
                                <tr key={j.JobLineID} style={{ backgroundColor: Number(j.IsHold) ? '#eee' : undefined }}>
                                    <td style={cell}>{j.RollSize}</td>
                                    <td style={cell}>{j.JobNumber}<PartBadge job={j} /></td>
                                    <td style={cell}>{j.PartyName}</td>
                                    <td style={cell}>{j.ItemName} · {measurementText(j)}</td>
                                    <td style={cell}>{formatNumberWithCommas(j.PlannedQty)}</td>
                                    <td style={cell}>
                                        <NumInput disabled={closed} value={j.MadeQty} width={80} placeholder={String(j.PlannedQty)} onSave={(v) => saveJob(j, { MadeQty: v })} />
                                        {!closed && (made === null || made === undefined) && !Number(j.IsHold) && (
                                            <Button size="sm" variant="link" className="p-0 ms-1" onClick={() => saveJob(j, { MadeQty: Number(j.PlannedQty) || 0 })}>{t('eqPlan')}</Button>
                                        )}
                                    </td>
                                    <td style={cell}><Form.Check disabled={closed} checked={!!Number(j.IsHold)} onChange={(e) => saveJob(j, { IsHold: e.target.checked ? 1 : 0 })} /></td>
                                    <td style={cell}>
                                        {(short || Number(j.IsHold)) ? (
                                            <TextInput disabled={closed} value={j.NotMadeReason} onSave={(v) => saveJob(j, { NotMadeReason: v })} placeholder={t('reasonPh')} />
                                        ) : <span className="text-muted">—</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>
            {!closed && (
                <Button size="sm" variant="outline-secondary" className="mb-3" onClick={() => sortedJobs.filter((j) => (j.MadeQty === null || j.MadeQty === undefined) && !Number(j.IsHold)).forEach((j) => saveJob(j, { MadeQty: Number(j.PlannedQty) || 0 }))}>
                    {t('fillMade')}
                </Button>
            )}

            <h6 className="mt-2">{t('secNotebook')}</h6>
            <div style={{ overflowX: 'auto' }}>
                <Table bordered size="sm" style={{ fontSize: 13, minWidth: 1100 }}>
                    <thead><tr>
                        <th style={head}>{t('colNo')}</th><th style={head}>{t('colRollNo')}</th><th style={head}>{t('colPaperSize')}</th><th style={head}>{t('colUse')}</th><th style={head}>{t('colPosition')}</th>
                        <th style={head}>{t('colUsedUp')}</th><th style={head}>{t('colBalance')}</th><th style={head}>{t('colCore')}</th><th style={head}>{t('colStartUsed')}</th>
                        <th style={head}>{t('colApproved')}</th><th style={head}>{t('colNote')}</th><th style={head}></th>
                    </tr></thead>
                    <tbody>
                        {rows.map((r, i) => {
                            const roll = rollMap[r.RollID];
                            const size = roll ? Number(roll.RollSize) : null;
                            const sep = r.UseKind === 'Board' && prevSize !== null && size !== prevSize;
                            if (r.UseKind === 'Board') prevSize = size;
                            const rowMsg = r.error ? (r.errorMsg ? t.msg(r.errorMsg) : r.error) : r.warning ? (r.warningMsg ? t.msg(r.warningMsg) : r.warning) : '';
                            return (
                                <tr key={r.key} style={{ borderTop: sep ? '3px solid #333' : undefined, backgroundColor: r.error ? '#f8d7da' : r.warning ? '#fff3cd' : undefined }}>
                                    <td style={cell}>{i + 1}</td>
                                    <td style={cell}><strong>{roll?.RollNo || roll?.RollCode || '?'}</strong></td>
                                    <td style={cell}>{rollText(roll)}<div className="text-muted small">{roll ? t('inStock', { kg: kg(roll.CurrentWeight) }) : ''}</div></td>
                                    <td style={cell}>
                                        <Form.Select size="sm" disabled={closed} value={r.UseKind} onChange={(e) => setRow(r.key, { UseKind: e.target.value })} style={{ minWidth: 110 }}>
                                            {options.useKinds.map((k) => <option key={k} value={k}>{t.kind(k)}</option>)}
                                        </Form.Select>
                                    </td>
                                    <td style={cell}>
                                        {r.UseKind === 'Board' ? (
                                            <>
                                                <Form.Select size="sm" disabled={closed} value={r.Position} onChange={(e) => setRow(r.key, { Position: e.target.value })} style={{ minWidth: 70 }}>
                                                    <option value="">?</option>
                                                    {positions.map((p) => <option key={p.Code} value={p.Code}>{p.Code}</option>)}
                                                </Form.Select>
                                                {size != null && !planSizes.includes(size) && !r.ShareRuns && !r.shareOpen && (
                                                    <Form.Select size="sm" disabled={closed} className="mt-1" value={r.RunSize === null ? '' : String(r.RunSize)} onChange={(e) => setRow(r.key, { RunSize: e.target.value })} style={{ minWidth: 110 }}>
                                                        <option value="">{r.runSize != null && r.RunSize === '' ? `${t('runAuto')} → ${r.runSize}"` : t('runAuto')}</option>
                                                        {planSizes.filter((s) => s <= size).map((s) => <option key={s} value={String(s)}>{t('runFor', { size: s })}</option>)}
                                                    </Form.Select>
                                                )}
                                                {/* one roll used by the jobs of several runs: tick them */}
                                                {size != null && planSizes.filter((s) => s <= size).length > 1 && (r.ShareRuns || r.shareOpen ? (
                                                    <div className="mt-1 small" title={t('shareTitle')} style={{ whiteSpace: 'nowrap' }}>
                                                        {planSizes.filter((s) => s <= size).sort((a, b) => a - b).map((s) => {
                                                            const picked = String(r.ShareRuns || '').split(',').filter(Boolean).map(Number);
                                                            return (
                                                                <Form.Check key={s} inline disabled={closed} id={`${r.key}-share-${s}`} label={`${s}"`} checked={picked.includes(s)}
                                                                    onChange={(e) => {
                                                                        const next = e.target.checked ? [...picked, s] : picked.filter((x) => x !== s);
                                                                        setRow(r.key, { ShareRuns: [...new Set(next)].sort((a, b) => a - b).join(','), RunSize: '' });
                                                                    }} />
                                                            );
                                                        })}
                                                    </div>
                                                ) : !closed && (
                                                    <Button size="sm" variant="link" className="p-0 small d-block" title={t('shareTitle')} onClick={() => setRow(r.key, { shareOpen: true })}>{t('shareLink')}</Button>
                                                ))}
                                            </>
                                        ) : <span className="text-muted">—</span>}
                                    </td>
                                    <td style={{ ...cell, textAlign: 'center' }}>
                                        <Form.Check disabled={closed} checked={r.IsFinished} onChange={(e) => setRow(r.key, { IsFinished: e.target.checked })} />
                                    </td>
                                    <td style={cell}>
                                        <Form.Control size="sm" disabled={closed || r.IsFinished} style={{ width: 80 }} value={r.IsFinished ? '' : r.EndKg} onChange={(e) => setRow(r.key, { EndKg: e.target.value })} />
                                    </td>
                                    <td style={cell}>
                                        {r.IsFinished ? <Form.Control size="sm" disabled={closed} style={{ width: 70 }} value={r.CoreKg} onChange={(e) => setRow(r.key, { CoreKg: e.target.value })} /> : <span className="text-muted">—</span>}
                                    </td>
                                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>{r.StartKg != null ? `${kg(r.StartKg)} → ${kg(r.UsedKg)}` : ''}</td>
                                    <td style={cell}>
                                        {r.UseKind === 'Board'
                                            ? <Form.Control size="sm" disabled={closed} style={{ width: 120 }} value={r.ApprovedBy} placeholder={t('swapPh')} onChange={(e) => setRow(r.key, { ApprovedBy: e.target.value })} />
                                            : <Form.Control size="sm" disabled={closed} style={{ width: 120 }} value={r.RefJob} placeholder={t('refJobPh')} onChange={(e) => setRow(r.key, { RefJob: e.target.value })} />}
                                    </td>
                                    <td style={cell}>
                                        <Form.Control size="sm" disabled={closed} style={{ width: 130 }} value={r.Note} onChange={(e) => setRow(r.key, { Note: e.target.value })} />
                                        {rowMsg && <div className={`small ${r.error ? 'text-danger' : 'text-warning'}`}>{rowMsg}</div>}
                                    </td>
                                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                                        {!closed && (
                                            <>
                                                <Button size="sm" variant="link" className="p-0 me-1" onClick={() => moveRow(i, -1)}>↑</Button>
                                                <Button size="sm" variant="link" className="p-0 me-1" onClick={() => moveRow(i, 1)}>↓</Button>
                                                <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => delRow(r.key)}>✕</Button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>
            {!closed && (
                <div className="mb-3" style={{ maxWidth: 520, position: 'relative' }}>
                    <InputGroup size="sm">
                        <Form.Control
                            ref={entryRef}
                            placeholder={t('rollEntryPh')}
                            value={entry}
                            onChange={(e) => setEntry(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    if (!matches.length) return;
                                    setPick((p) => (e.key === 'ArrowDown' ? Math.min(p + 1, matches.length - 1) : Math.max(p - 1, 0)));
                                    return;
                                }
                                if (e.key === 'Escape') { setEntry(''); return; }
                                if (e.key !== 'Enter') return;
                                const roll = sureMatch();
                                if (roll) addRoll(roll);
                                else if (!matches.length) notify.error(t('rollNotFound'));
                                else notify.error(t('rollPickOne'));
                            }}
                        />
                        <Button variant="outline-primary" onClick={() => addRoll(sureMatch())}>{t('addRoll')}</Button>
                    </InputGroup>
                    {entry.trim() && (
                        <div style={{ position: 'absolute', zIndex: 5, background: 'white', border: '1px solid #ccc', width: '100%', maxHeight: 320, overflowY: 'auto' }}>
                            {!matches.length && <div className="text-muted" style={{ padding: '3px 8px' }}>{t('rollNotFound')}</div>}
                            {matches.map((m, i) => {
                                const r = m.roll;
                                return (
                                    <div
                                        key={r.RollID}
                                        style={{ padding: '3px 8px', cursor: 'pointer', backgroundColor: i === pick ? '#cfe2ff' : m.weightHit && m.dist === 0 ? '#d1e7dd' : undefined }}
                                        onMouseEnter={() => setPick(i)}
                                        onMouseDown={(e) => { e.preventDefault(); addRoll(r); }}
                                    >
                                        <strong>{Number(r.RollSize)}"</strong> · <strong>{kg(r.CurrentWeight)} kg</strong> · {paperName(r.GSMID)}
                                        <span className="text-muted"> · {t('rollNoShort')} {r.RollNo || r.RollCode}</span>
                                        {usedToday.has(r.RollID) && <Badge bg="secondary" className="ms-1">{t('rollInList')}</Badge>}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <h6 className="mt-2">{t('secCheck')}</h6>
            {!closed && (
                <div className="d-flex gap-2 mb-2">
                    <Button size="sm" variant="outline-primary" onClick={saveNotebook} disabled={saving || !dirty}>{saving ? t('saving') : dirty ? t('save') : t('saved')}</Button>
                    <Button size="sm" variant="primary" onClick={runCheck} disabled={saving}>{t('check')}</Button>
                    <Button size="sm" variant="success" onClick={closeDay} disabled={!canClose || closing}>{closing ? t('closing') : t('closeDay')}</Button>
                    {!check && <span className="text-muted small align-self-center">{t('pressCheck')}</span>}
                    {check && dirty && <span className="text-muted small align-self-center">{t('changed')}</span>}
                </div>
            )}
            {closed && options.isAdmin && <Button size="sm" variant="outline-danger" className="mb-2" onClick={reopen}>{t('reopen')}</Button>}

            {check && (
                <>
                    {(check.errors || []).length > 0 && (
                        <Alert variant="danger" className="py-2">
                            <strong>{t('fixFirst')}</strong>
                            <ul className="mb-0">{check.errors.map((e, i) => <li key={i}>{t.msg(e)}</li>)}</ul>
                        </Alert>
                    )}
                    {(check.warnings || []).length > 0 && (
                        <Alert variant="warning" className="py-2">
                            <ul className="mb-0">{check.warnings.map((w, i) => <li key={i}>{t.msg(w)}</li>)}</ul>
                        </Alert>
                    )}
                    {check.totals && (
                        <div className="mb-2 small">
                            {t('totals', {
                                board: kg(check.totals.boardKg), theory: kg(check.totals.theoryKg), trim: kg(check.totals.trimKg), oversize: kg(check.totals.oversizeKg),
                                waste: pct(check.totals.wastePct), other: kg(check.totals.otherKg), core: kg(check.totals.coreKg), cores: kg(check.totals.coresKg), cost: kg(check.totals.costTk),
                            })}
                        </div>
                    )}
                    {/* one reason for the whole day, required only when the day's wastage is out of range */}
                    {check.totals && (closed ? (check.totals.reason ? <div className="mb-2 small"><strong>{t('dayReason')}:</strong> {check.totals.reason}</div> : null) : (
                        <Form.Group className="mb-2" style={{ maxWidth: 1000 }}>
                            <Form.Label className="small mb-1"><strong>{t('dayReason')}</strong> ({t('dayWastage')} <span style={{ backgroundColor: VERDICT_BG[check.totals.verdict], padding: '0 4px' }}>{pct(check.totals.wastePct)}</span>)</Form.Label>
                            <Form.Control as="textarea" rows={2} size="sm" value={reasons['0'] || ''}
                                placeholder={check.totals.needsReason ? t('reasonRequired') : t('optional')}
                                isInvalid={check.totals.needsReason && !(reasons['0'] || '').trim()}
                                onChange={(e) => { setReasons((x) => ({ ...x, 0: e.target.value })); setDirty(true); }} />
                        </Form.Group>
                    ))}
                    <Table bordered size="sm" style={{ fontSize: 13, maxWidth: 1000 }}>
                        <thead><tr>
                            <th style={head}>{t('colRun')}</th><th style={head}>{t('colPosition')}</th><th style={head}>{t('colExpected')}</th><th style={head}>{t('colTrim')}</th><th style={head}>{t('colUsed')}</th><th style={head}>{t('colWastage')}</th><th style={head}>{t('colRunReason')}</th>
                        </tr></thead>
                        <tbody>
                            {(check.runs || []).map((run) => (
                                <React.Fragment key={run.size}>
                                    {run.positions.map((p, i) => (
                                        <tr key={p.Position}>
                                            {i === 0 && <td style={cell} rowSpan={run.positions.length + 1}><strong>{run.size}"</strong><div className="text-muted small">{t('jobsCount', { n: run.jobCount })}</div></td>}
                                            <td style={cell}>{p.Position}</td>
                                            <td style={cell}>{kg(p.TheoryKg)}</td>
                                            <td style={cell}>{kg(p.TrimKg)}{Number(p.OversizeKg) > 0 && <div className="text-muted small">{t('oversizeShort')} {kg(p.OversizeKg)}</div>}</td>
                                            <td style={cell}>{kg(p.ActualKg)}</td>
                                            <td style={{ ...cell, backgroundColor: VERDICT_BG[p.verdict] }}>{pct(p.WastePct)}</td>
                                            {i === 0 && (
                                                <td style={cell} rowSpan={run.positions.length + 1}>
                                                    {closed ? (run.reason || '') : (
                                                        <Form.Control as="textarea" rows={2} size="sm" value={reasons[String(r2(run.size))] || ''}
                                                            placeholder={t('optional')}
                                                            onChange={(e) => { setReasons((x) => ({ ...x, [String(r2(run.size))]: e.target.value })); setDirty(true); }} />
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                    <tr style={{ fontWeight: 'bold' }}>
                                        <td style={cell}>{t('total')}</td>
                                        <td style={cell}>{kg(run.TheoryKg)}</td>
                                        <td style={cell}>{kg(run.TrimKg)}{run.TrimPct != null ? ` (${run.TrimPct}%)` : ''}{Number(run.OversizeKg) > 0 && <div className="text-muted small">{t('oversizeShort')} {kg(run.OversizeKg)}</div>}</td>
                                        <td style={cell}>{kg(run.ActualKg)}</td>
                                        <td style={{ ...cell, backgroundColor: VERDICT_BG[run.verdict] }}>{pct(run.WastePct)}</td>
                                    </tr>
                                </React.Fragment>
                            ))}
                        </tbody>
                    </Table>
                    <div className="text-muted small mb-4">
                        {t('wasteHelp', { ok: options.limits?.WASTE_OK_PCT ?? 5, warn: options.limits?.WASTE_WARN_PCT ?? 8, low: options.limits?.WASTE_LOW_PCT ?? -2 })}
                    </div>
                </>
            )}
        </>
    );
}

function TextInput({ value, onSave, disabled, placeholder }) {
    const [v, setV] = useState(value ?? '');
    useEffect(() => { setV(value ?? ''); }, [value]);
    return (
        <Form.Control size="sm" value={v} disabled={disabled} placeholder={placeholder} onChange={(e) => setV(e.target.value)}
            onBlur={() => { if ((v || '') !== (value || '')) onSave(v); }} />
    );
}

export default BoardPlan;
